import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { format, isEqual } from "date-fns";
import { useLocale } from "@/hooks/useLocale";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { X, Clock, Loader2, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useTranslation } from "react-i18next";

interface AvailabilitySetting {
  id: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_available: boolean;
}

interface BlockedDate {
  id: string;
  blocked_date: string;
  reason: string | null;
}

export default function AvailabilitySettings() {
  const { toast } = useToast();
  const { t } = useTranslation();
  const { dateFnsLocale } = useLocale();


  const dayNames = [
    t('settings.sunday'),
    t('settings.monday'),
    t('settings.tuesday'),
    t('settings.wednesday'),
    t('settings.thursday'),
    t('settings.friday'),
    t('settings.saturday')
  ];
  const [weeklySchedule, setWeeklySchedule] = useState<AvailabilitySetting[]>([]);
  const [blockedDates, setBlockedDates] = useState<BlockedDate[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [blockReason, setBlockReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [blockingDate, setBlockingDate] = useState(false);
  const [dateError, setDateError] = useState<string | null>(null);

  useEffect(() => {
    fetchAvailabilitySettings();
    fetchBlockedDates();
  }, []);



  const blockedDateObjects = useMemo(() => {
    return blockedDates.map(date => new Date(date.blocked_date));
  }, [blockedDates]);

  const isDateBlocked = (date: Date): boolean => {
    return blockedDateObjects.some(blockedDate => 
      isEqual(
        new Date(blockedDate.setHours(0, 0, 0, 0)),
        new Date(date.setHours(0, 0, 0, 0))
      )
    );
  };

  const handleDateSelect = (date: Date | undefined) => {
    setDateError(null);
    
    if (date) {
      if (isDateBlocked(new Date(date))) {
        setDateError(t('settings.dateAlreadyBlocked'));
        return;
      }
    }
    
    setSelectedDate(date);
  };

  const fetchAvailabilitySettings = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('No authenticated user');
      }

      const { data, error } = await supabase
        .from('availability_settings')
        .select('*')
        .eq('user_id', user.id)
        .order('day_of_week');

      if (error) throw error;

      if (!data || data.length === 0) {
        await initializeDefaultSettings(user.id);
        await fetchAvailabilitySettings();
        return;
      }

      setWeeklySchedule(data);
    } catch (error) {
      console.error('Error fetching availability settings:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch availability settings",
      });
    } finally {
      setLoading(false);
    }
  };

  const initializeDefaultSettings = async (userId: string) => {
    try {
      const defaultSettings = Array.from({ length: 7 }, (_, i) => ({
        user_id: userId,
        day_of_week: i,
        start_time: '09:00',
        end_time: '17:00',
        is_available: i !== 0 && i !== 6,
      }));

      const { error } = await supabase
        .from('availability_settings')
        .insert(defaultSettings);

      if (error) throw error;
    } catch (error) {
      console.error('Error initializing availability settings:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to initialize availability settings",
      });
    }
  };

  const fetchBlockedDates = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('blocked_dates')
        .select('*')
        .eq('user_id', user.id)
        .order('blocked_date');

      if (error) throw error;

      setBlockedDates(data || []);
    } catch (error) {
      console.error('Error fetching blocked dates:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch blocked dates",
      });
    }
  };

  const updateDayAvailability = async (dayOfWeek: number, isAvailable: boolean) => {
    try {
      setUpdating(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('availability_settings')
        .update({ is_available: isAvailable })
        .eq('user_id', user.id)
        .eq('day_of_week', dayOfWeek);

      if (error) throw error;

      setWeeklySchedule(weeklySchedule.map(s =>
        s.day_of_week === dayOfWeek ? { ...s, is_available: isAvailable } : s
      ));

      toast({
        title: "Success",
        description: `${dayNames[dayOfWeek]} ${t('settings.availabilityUpdated')}`,
      });
    } catch (error) {
      console.error('Error updating availability:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update availability",
      });
    } finally {
      setUpdating(false);
    }
  };

  const updateDayTimes = async (dayOfWeek: number, field: 'start_time' | 'end_time', value: string) => {
    try {
      setUpdating(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('availability_settings')
        .update({ [field]: value })
        .eq('user_id', user.id)
        .eq('day_of_week', dayOfWeek);

      if (error) throw error;

      setWeeklySchedule(weeklySchedule.map(s =>
        s.day_of_week === dayOfWeek ? { ...s, [field]: value } : s
      ));

      toast({
        title: "Success",
        description: `${dayNames[dayOfWeek]} ${t('settings.timesUpdated')}`,
      });
    } catch (error) {
      console.error('Error updating time:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update time",
      });
    } finally {
      setUpdating(false);
    }
  };

  const blockDate = async () => {
    if (!selectedDate) return;

    try {
      setBlockingDate(true);
      
      if (isDateBlocked(new Date(selectedDate))) {
        setDateError(t('settings.dateAlreadyBlocked'));
        setBlockingDate(false);
        return;
      }
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('blocked_dates')
        .insert({
          blocked_date: format(selectedDate, 'yyyy-MM-dd'),
          reason: blockReason,
          user_id: user.id
        });

      if (error) throw error;

      await fetchBlockedDates();
      setSelectedDate(undefined);
      setBlockReason('');
      
      toast({
        title: "Success",
        description: t('settings.dateBlocked'),
      });
    } catch (error) {
      console.error('Error blocking date:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to block date",
      });
    } finally {
      setBlockingDate(false);
    }
  };

  const unblockDate = async (id: string) => {
    try {
      setUpdating(true);
      const { error } = await supabase
        .from('blocked_dates')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setBlockedDates(blockedDates.filter(d => d.id !== id));
      
      toast({
        title: "Success",
        description: t('settings.dateUnblocked'),
      });
    } catch (error) {
      console.error('Error unblocking date:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to unblock date",
      });
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8 h-[50vh]">
        <img 
          src="/lovable-uploads/f8714beb-a432-4da6-acab-3ef6f0a42367.png" 
          alt="Loading availability settings" 
          className="h-10 w-10 gebeya-spinner" 
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle>{t('settings.weeklySchedule')}</CardTitle>
          </CardHeader>
          <CardContent className="p-3 sm:p-6 space-y-3 sm:space-y-4">
            {weeklySchedule.map((setting) => (
              <div 
                key={setting.day_of_week} 
                className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 border border-border rounded-lg hover:border-gebeya-pink/40 transition-colors space-y-2 sm:space-y-0 bg-card"
              >
                <div className="flex items-center gap-2 sm:gap-4">
                  <Switch
                    checked={setting.is_available}
                    onCheckedChange={(checked) => updateDayAvailability(setting.day_of_week, checked)}
                    className="data-[state=checked]:bg-gebeya-pink"
                    disabled={updating}
                  />
                  <div className="flex items-center gap-1 sm:gap-2">
                    <Clock className="h-4 w-4 text-gebeya-pink" />
                    <Label className="font-medium text-gebeya-purple text-sm sm:text-base">{dayNames[setting.day_of_week]}</Label>
                  </div>
                </div>
                
                {setting.is_available && (
                  <div className="flex flex-wrap items-center gap-2 ml-8 sm:ml-0">
                    <Input
                      type="time"
                      value={setting.start_time}
                      onChange={(e) => updateDayTimes(setting.day_of_week, 'start_time', e.target.value)}
                      className="w-24 sm:w-32 border-border focus:border-gebeya-pink focus:ring-gebeya-pink text-sm"
                      disabled={updating}
                    />
                    <span className="text-gebeya-purple text-sm sm:text-base whitespace-nowrap px-1">{t('common.to')}</span>
                    <Input
                      type="time"
                      value={setting.end_time}
                      onChange={(e) => updateDayTimes(setting.day_of_week, 'end_time', e.target.value)}
                      className="w-24 sm:w-32 border-border focus:border-gebeya-pink focus:ring-gebeya-pink text-sm"
                      disabled={updating}
                    />
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle>{t('settings.blockDates')}</CardTitle>
          </CardHeader>
          <CardContent className="p-3 sm:p-6 space-y-4 sm:space-y-6">
            <div className="bg-card p-2 sm:p-4 rounded-lg border border-border overflow-x-auto">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={handleDateSelect}
                locale={dateFnsLocale}
                className="rounded-md pointer-events-auto max-w-full"
                disabled={(date) => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  
                  const checkDate = new Date(date);
                  checkDate.setHours(0, 0, 0, 0);

                  return checkDate < today || isDateBlocked(new Date(date));
                }}
                classNames={{
                  day_selected: "bg-gebeya-pink text-white hover:bg-gebeya-pink/90",
                  day_today: "bg-gebeya-orange/20 text-gebeya-orange",
                  nav_button: "h-7 w-7 sm:h-9 sm:w-9",
                  caption: "text-sm sm:text-base",
                  cell: "h-8 w-8 sm:h-10 sm:w-10 text-xs sm:text-sm p-0",
                  table: "w-full border-collapse",
                }}
              />
            </div>
            
            {dateError && (
              <Alert variant="destructive" className="mt-2">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{dateError}</AlertDescription>
              </Alert>
            )}
            
            {selectedDate && (
              <Card className="border border-border p-3 sm:p-4">
                <CardContent className="p-0 space-y-3">
                  <p className="font-medium text-gebeya-purple text-sm sm:text-base">
                    {t('settings.selectDateToBlock')} {format(selectedDate, 'MMMM d, yyyy', { locale: dateFnsLocale })}
                  </p>
                  <Textarea
                    placeholder={t('settings.reasonOptional')}
                    value={blockReason}
                    onChange={(e) => setBlockReason(e.target.value)}
                    className="border-border focus:border-gebeya-pink focus:ring-gebeya-pink resize-none text-sm"
                    disabled={blockingDate}
                  />
                  <Button 
                    onClick={blockDate} 
                    className="w-full bg-gradient-to-r from-gebeya-pink to-gebeya-orange text-white hover:opacity-90 text-sm sm:text-base py-2 h-auto"
                    disabled={blockingDate}
                  >
                    {blockingDate ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {t('common.loading')}
                      </>
                    ) : (
                      t('settings.blockDate')
                    )}
                  </Button>
                </CardContent>
              </Card>
            )}
            
            <div className="space-y-2 sm:space-y-3">
              <h3 className="font-medium text-gebeya-purple text-sm sm:text-base">{t('settings.blockedDates')}</h3>
              <div className="max-h-64 sm:max-h-80 overflow-y-auto pr-1 space-y-2">
                {blockedDates.length === 0 ? (
                  <p className="text-gray-500 text-xs sm:text-sm italic">{t('settings.noBlockedDates')}</p>
                ) : (
                  blockedDates.map((blocked) => (
                    <div 
                      key={blocked.id} 
                      className="flex items-center justify-between p-2 sm:p-3 border border-border rounded-lg hover:border-gebeya-pink/40 transition-colors bg-card"
                    >
                      <div>
                        <p className="font-medium text-gebeya-purple text-xs sm:text-sm">
                          {format(new Date(blocked.blocked_date), 'MMMM d, yyyy', { locale: dateFnsLocale })}
                        </p>
                        {blocked.reason && (
                          <p className="text-xs text-gray-500 mt-1">{blocked.reason}</p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => unblockDate(blocked.id)}
                        className="text-gebeya-pink hover:text-gebeya-orange hover:bg-transparent p-1 h-auto"
                        disabled={updating}
                      >
                        <X className="h-3 w-3 sm:h-4 sm:w-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
